import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { CheckCircle2, Clock, UserPlus } from "lucide-react";
import { CaseParticipantType, ParticipantRef } from "../dtos";
import { useCaseParticipants } from "../hooks/use-participants.hook";
import AddParticipantButton from "./add-participant-button";

const ROW_LABEL: Record<CaseParticipantType, string> = {
  PRIMARY: "Applicant",
  JOINT: "Joint Client",
  GUARANTOR: "Guarantor",
};

/**
 * The case's applicants, mirroring workflow-manager's co-applicants panel: PRIMARY +
 * joint/guarantor rows with their §PhoneLinking chip (Linked = real app account;
 * Pending signup = phone-only placeholder that auto-links when they sign up). Renders
 * inside the Client Profile step (uiFlags.participantCapture, like the workflow itself).
 *
 * §CoApplicantEntry: this card owns the add actions for BOTH co-applicant types. The guarantor
 * button used to float in the Loan process header, away from the list it adds to — so the two
 * halves of the same job lived in different places and the empty state had to explain where the
 * other one was.
 */
const CoApplicantsCard = ({
  caseUid,
  addTypes = [],
}: {
  caseUid: string | null;
  addTypes?: Extract<CaseParticipantType, "JOINT" | "GUARANTOR">[];
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  const participants = useCaseParticipants(caseUid);

  const rows: ParticipantRef[] = participants.data ?? [];
  const coApplicants = rows.filter((p) => p.type !== "PRIMARY");

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: theme.nawy.radius.lg,
        border: `1px solid ${colors.semantic.border.primary}`,
        p: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        // §Mobile: title + "Add joint client" don't share a squeezed row — wrap whole controls
        sx={{ mb: 1.5, flexWrap: "wrap", rowGap: 1, columnGap: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <UserPlus size={18} color={colors.semantic.text.primary} />
          <Typography
            sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            Co-applicants
          </Typography>
        </Stack>
        {addTypes.length > 0 && (
          <Stack direction="row" spacing={1}>
            {addTypes.map((type) => (
              <AddParticipantButton key={type} caseUid={caseUid} type={type} />
            ))}
          </Stack>
        )}
      </Stack>

      {participants.isLoading ? (
        <CircularProgress size={18} />
      ) : (
        <Stack spacing={1.25}>
          {rows.map((participant) => (
            <Stack
              key={participant.uid}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 14, lineHeight: "20px", fontWeight: 600 }}>
                    {participant.customer?.name || participant.customer?.phone}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={ROW_LABEL[participant.type] ?? participant.type}
                  />
                </Stack>
                {participant.customer?.phone && (
                  <Typography
                    variant="body2"
                    color={colors.semantic.text.secondary}
                  >
                    {participant.customer.phone}
                  </Typography>
                )}
              </Box>
              {participant.linkStatus === "LINKED" ? (
                <Chip
                  size="small"
                  color="success"
                  icon={<CheckCircle2 size={14} />}
                  label="Linked"
                />
              ) : (
                <Chip
                  size="small"
                  color="warning"
                  variant="outlined"
                  icon={<Clock size={14} />}
                  label="Pending signup"
                />
              )}
            </Stack>
          ))}
          {!coApplicants.length && (
            <Typography variant="body2" color={colors.semantic.text.secondary}>
              No co-applicants yet. Add a joint client or a guarantor above —
              they’ll link to their app account automatically once they sign up.
            </Typography>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default CoApplicantsCard;
